import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Search, Users, Plus, Pencil, Trash2, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const EMPTY = { employee_number: "", first_name: "", last_name: "", active: true };

export function EmployeesPanel() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create
  const [form, setForm] = useState(EMPTY);
  const [toDelete, setToDelete] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["employees", search],
    queryFn: async () => (await api.get("/recon/employees", { params: search ? { search } : {} })).data,
    keepPreviousData: true,
  });
  const employees = data?.items || [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["employees"] });

  const saveMut = useMutation({
    mutationFn: (payload) =>
      editing ? api.put(`/recon/employees/${editing.id}`, payload) : api.post("/recon/employees", payload),
    onSuccess: () => { invalidate(); setDialogOpen(false); toast.success(editing ? "Empleado actualizado" : "Empleado creado"); },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail) || "No se pudo guardar"),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/recon/employees/${id}`),
    onSuccess: () => { invalidate(); setToDelete(null); toast.success("Empleado eliminado"); },
    onError: () => toast.error("No se pudo eliminar"),
  });

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({ employee_number: e.employee_number, first_name: e.first_name || "", last_name: e.last_name || "", active: e.active });
    setDialogOpen(true);
  };
  const submit = (ev) => { ev.preventDefault(); saveMut.mutate(form); };

  return (
    <div className="space-y-4" data-testid="employees-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Users size={18} /> Empleados
          </h2>
          <p className="text-sm text-slate-500 mt-1">Vendedores reconocidos cruzando el catálogo con las órdenes (Salesperson).</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="employees-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Número o nombre…" className="h-9 rounded-sm border-slate-300 bg-white text-sm pl-8 w-[220px]" />
          </div>
          <Button data-testid="employee-new-button" onClick={openCreate} className="h-9 rounded-sm bg-[#0F172A] hover:bg-[#0F172A]/90 gap-2">
            <Plus size={15} /> Nuevo
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-sm">
        <div className="overflow-auto thin-scroll">
          <table className="w-full text-sm min-w-[680px]">
            <thead>
              <tr className="bg-slate-50 border-b border-[#E2E8F0] text-left">
                {["#", "Nombre", "Apellido", "Órdenes", "Estado", "Acciones"].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-[11px] uppercase tracking-[0.05em] font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={6} className="py-14 text-center text-slate-400"><Loader2 className="animate-spin inline" size={20} /></td></tr>
              ) : employees.length === 0 ? (
                <tr><td colSpan={6} className="py-14 text-center text-slate-400 text-sm">Sin empleados</td></tr>
              ) : employees.map((e) => (
                <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`employee-row-${e.id}`}>
                  <td className="px-3 py-2.5 font-mono text-slate-900">{e.employee_number}</td>
                  <td className="px-3 py-2.5 text-slate-700">{e.first_name || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-700">{e.last_name || "—"}</td>
                  <td className="px-3 py-2.5 font-mono text-slate-500">{e.order_count ?? 0}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-sm border px-2 py-0.5 ${e.active ? "bg-[#D1FAE5] text-[#065F46] border-[#34D399]" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                      {e.active ? <BadgeCheck size={12} /> : null}{e.active ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" data-testid={`employee-edit-${e.id}`} onClick={() => openEdit(e)} className="h-7 rounded-sm text-xs gap-1">
                        <Pencil size={12} /> Editar
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`employee-delete-${e.id}`} onClick={() => setToDelete(e)} className="h-7 w-7 p-0 rounded-sm text-[#991B1B]">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-[#E2E8F0] text-xs text-slate-400 font-mono" data-testid="employees-count">
          {employees.length} empleados
        </div>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">{editing ? "Editar empleado" : "Nuevo empleado"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Número de empleado</Label>
              <Input data-testid="employee-number-input" value={form.employee_number}
                onChange={(e) => setForm({ ...form, employee_number: e.target.value })}
                placeholder="p. ej. 107" className="rounded-sm font-mono" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Nombre</Label>
                <Input data-testid="employee-firstname-input" value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Apellido</Label>
                <Input data-testid="employee-lastname-input" value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-sm" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-sm border border-[#E2E8F0] px-3 py-2.5">
              <span className="text-sm text-slate-700">Empleado activo</span>
              <Switch data-testid="employee-active-switch" checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancelar</Button>
              <Button type="submit" data-testid="employee-save-button" disabled={saveMut.isPending}
                className="rounded-sm bg-[#0F172A] hover:bg-[#0F172A]/90 gap-2">
                {saveMut.isPending ? <Loader2 size={15} className="animate-spin" /> : null}
                {editing ? "Guardar cambios" : "Crear empleado"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="employee-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar empleado?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <span className="font-semibold">#{toDelete?.employee_number} · {toDelete?.name}</span>. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="employee-delete-confirm" onClick={() => deleteMut.mutate(toDelete.id)}
              className="rounded-sm bg-[#991B1B] hover:bg-[#991B1B]/90">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
